import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  Fade,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ChevronLeft,
  ChevronRight,
  Close,
  ExpandLess,
  ExpandMore,
  Refresh,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../../store/Index";
import {
  MemoryBook,
  setDailyGoal,
  setSelectedBook,
} from "../../../../../store/slice/MemoryBookSlice";
import { fetchAssignedMemoryBooks } from "./service/memoryBook.api";

interface MemoryBookLoginModalProps {
  open: boolean;
  onClose: () => void;
}

const MemoryBookLoginModal: React.FC<MemoryBookLoginModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);

  const [step, setStep] = useState<"select" | "goal">("select");
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [goalInput, setGoalInput] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const [books, setBooks] = useState<MemoryBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInProgress, setShowInProgress] = useState(false);
  const [isSubmittingGoal, setIsSubmittingGoal] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const scroll = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  const updateScrollState = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setIsAtStart(scrollLeft <= 1);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;

    updateScrollState();

    const handleResize = () => updateScrollState();
    const handleScroll = () => updateScrollState();

    window.addEventListener("resize", handleResize);
    scrollElement?.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      scrollElement?.removeEventListener("scroll", handleScroll);
    };
  }, [books]);

  useEffect(() => {
    if (!open) return;

    const loadBooks = async () => {
      const isInitialLoad = books.length === 0 && !shouldRefresh;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      try {
        if (!user?.id) {
          throw new Error("User not found");
        }

        const res = await fetchAssignedMemoryBooks(user.id);
        const results = res.data?.results ?? [];

        const mappedBooks: MemoryBook[] = results.map((assignment) => {
          const physicalComments = assignment.physical_comments || 0;
          const doneComments = assignment.done_comments || 0;
          const totalComments = physicalComments;
          const unfinished = Math.max(totalComments - doneComments, 0);

          const deceasedName = assignment.stream?.booking?.deceased?.[0]
            ? `${assignment.stream.booking.deceased[0].first_name} ${assignment.stream.booking.deceased[0].surname}`
            : "Unnamed Book";

          const streamTitle = assignment.stream?.title || "Untitled Stream";
          const deceasedPhoto =
            assignment.stream?.booking?.deceased?.[0]?.person_image;

          return {
            id: String(assignment.stream?.id),
            assignmentId: assignment.id,
            title: streamTitle || deceasedName,
            thumbnail: deceasedPhoto,
            physical_comments: physicalComments,
            total_comments: totalComments,
            unfinished_comments: unfinished,
            dueDate: assignment.due_date,
            assignedAt: assignment.assigned_at,
            status:
              unfinished === 0
                ? "completed"
                : doneComments > 0
                  ? "in_progress"
                  : "not_started",
          };
        });

        setBooks(mappedBooks);
        setShouldRefresh(false);

        if (
          selectedBookId &&
          !mappedBooks.find((b) => b.id === selectedBookId)
        ) {
          setSelectedBookId(null);
          setStep("select");
        }
      } catch {
        setError("Failed to load assigned memory books. Please try again.");
        setBooks([]);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };

    loadBooks();
  }, [open, shouldRefresh, user?.id, books.length, selectedBookId]);

  const handleRefresh = () => {
    setShouldRefresh(true);
  };

  const handleSelectBook = (book: MemoryBook) => {
    if (book.total_comments <= 0) return;

    setSelectedBookId(book.id);
    setStep("goal");
  };

  const handleSetGoal = async () => {
    if (isSubmittingGoal) return;

    const goal = parseInt(goalInput, 10);

    if (
      !selectedBook ||
      isNaN(goal) ||
      goal <= 0 ||
      goal > selectedBook.unfinished_comments
    ) {
      return;
    }

    if (!selectedBook.id) {
      setError("Failed to select book. Please try again.");
      return;
    }

    setIsSubmittingGoal(true);

    try {
      dispatch(setDailyGoal(goal));
      dispatch(setSelectedBook(selectedBook));

      setStep("select");
      setSelectedBookId(null);
      setGoalInput("");
      setError(null);
      onClose();

      navigate("/memory-book-dashboard");
    } catch {
      setError("Failed to set goal. Please try again.");
    } finally {
      setIsSubmittingGoal(false);
    }
  };

  const handleClose = () => {
    onClose();
    setStep("select");
    setSelectedBookId(null);
    setGoalInput("");
    setError(null);
    setIsSubmittingGoal(false);
  };

  const selectedBook = books.find((b) => b.id === selectedBookId);

  const getDaysRemaining = (dueDate?: string): number | null => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getDueDateColor = (days: number | null): string => {
    if (days === null) return "#6b6b6b";
    if (days < 0) return "#d32f2f";
    if (days <= 5) return "#f57c00";
    return "#388e3c";
  };

  const formatDueLabel = (days: number | null, dueDate?: string): string => {
    if (days === null || !dueDate) return "No due date";
    if (days < 0) return `Overdue by ${Math.abs(days)}d`;
    if (days === 0) return "Due today";
    if (days === 1) return "Due tomorrow";
    return `${days} days left`;
  };

  const getDueDateBarProgress = (
    assignedAt?: string,
    dueDate?: string,
  ): number => {
    if (!assignedAt || !dueDate) return 0;
    const start = new Date(assignedAt).getTime();
    const end = new Date(dueDate).getTime();
    const totalDuration = end - start;
    if (totalDuration <= 0) return 100;
    const elapsed = Date.now() - start;
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
  };

  const DueDateBar: React.FC<{ dueDate?: string; assignedAt?: string }> = ({ dueDate, assignedAt }) => {
    const days = getDaysRemaining(dueDate);
    const color = getDueDateColor(days);
    const label = formatDueLabel(days, dueDate);
    const barPct = getDueDateBarProgress(assignedAt, dueDate);

    return (
      <Box mt={1.5}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.7rem" }}
          >
            Due date
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color, fontSize: "0.7rem" }}
          >
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: 6,
            backgroundColor: "rgba(0,0,0,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${barPct}%`,
              height: "100%",
              borderRadius: 3,
              backgroundColor: color,
              transition: "width 0.4s ease",
            }}
          />
        </Box>
        {dueDate && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mt: 0.5,
              textAlign: "right",
              fontSize: "0.68rem",
            }}
          >
            {new Date(dueDate).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Typography>
        )}
      </Box>
    );
  };

  const goal = parseInt(goalInput, 10);
  const goalError =
    !!goalInput &&
    (!selectedBook ||
      isNaN(goal) ||
      goal <= 0 ||
      goal > selectedBook.unfinished_comments);

  const notStartedBooks = books.filter((book) => book.status === "not_started");
  const inProgressBooks = books.filter((book) => book.status === "in_progress");

  return (
    <Dialog
      open={open}
      TransitionComponent={Fade}
      keepMounted
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "75vw",
          height: "75vh",
          maxWidth: "900px",
          borderRadius: 2,
          p: 0,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {step === "select" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
                display: "flex",
                gap: 1.5,
              }}
            >
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  size="small"
                >
                  {isRefreshing ? <CircularProgress size={18} /> : <Refresh />}
                </IconButton>
              </Tooltip>

              <IconButton onClick={handleClose} aria-label="Close">
                <Close />
              </IconButton>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
              }}
            >
              Welcome back,{" "}
              {user?.first_name ||
                user?.first_name ||
                user?.username
                  ?.split("@")[0]
                  ?.replace(/^\w/, (c) => c.toUpperCase())}
              !
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Since you last logged in the following projects are available.
            </Typography>

            {loading && books.length === 0 && (
              <Box textAlign="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={2}>
                  Loading Memory Books...
                </Typography>
              </Box>
            )}

            {error && (
              <Box sx={{ mb: 2 }}>
                <Typography color="error" textAlign="center" sx={{ py: 2 }}>
                  {error}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRefresh}
                  sx={{ display: "block", mx: "auto" }}
                >
                  Try Again
                </Button>
              </Box>
            )}

            {!loading &&
              !error &&
              notStartedBooks.length === 0 &&
              inProgressBooks.length === 0 && (
                <Typography
                  textAlign="center"
                  sx={{ py: 4 }}
                  color="text.secondary"
                >
                  No memory books available at the moment.
                </Typography>
              )}

            <Box position="relative" width="100%" sx={{ px: 4 }}>
              {!isAtStart && notStartedBooks.length > 0 && (
                <IconButton
                  onClick={() => scroll("left")}
                  aria-label="Scroll left"
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: reduxTheme.primary_colors?.[800],
                    boxShadow: 3,
                  }}
                >
                  <ChevronLeft fontSize="large" />
                </IconButton>
              )}
              <Box
                ref={scrollRef}
                sx={{
                  display: "flex",
                  gap: 2,
                  py: 2,
                  overflowX: "auto",
                  scrollSnapType: "x mandatory",
                  width: "100%",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                {notStartedBooks.map((book) => {
                  return (
                    <Box
                      key={book.id}
                      sx={{
                        minWidth: 244,
                        scrollSnapAlign: "start",
                        backgroundColor: reduxTheme.primary_colors?.[800],
                        p: 2,
                        borderRadius: 2,
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                      >
                        <Avatar
                          src={book.thumbnail || `./placeholder.svg`}
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            border: "3px solid rgba(107, 76, 59, 0.15)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />

                        <Typography
                          variant="body2"
                          textAlign="center"
                          sx={{ fontWeight: 600 }}
                        >
                          {book.title}
                        </Typography>
                      </Box>

                      <Box mt={2} display="flex" flexDirection="column" gap={1}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            p: 1.5,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                          >
                            Comments
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {book.physical_comments ?? 0}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Due date bar */}
                      <DueDateBar dueDate={book.dueDate} assignedAt={book.assignedAt} />

                      <Button
                        fullWidth
                        sx={{ mt: 2 }}
                        variant="contained"
                        disabled={book.total_comments <= 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectBook(book);
                        }}
                      >
                        Start
                      </Button>
                    </Box>
                  );
                })}
              </Box>
              {!isAtEnd && notStartedBooks.length > 0 && (
                <IconButton
                  onClick={() => scroll("right")}
                  aria-label="Scroll right"
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: reduxTheme.primary_colors?.[800],
                    boxShadow: 3,
                  }}
                >
                  <ChevronRight fontSize="large" />
                </IconButton>
              )}
            </Box>

            {inProgressBooks.length > 0 && (
              <Box mt={0} position="relative">
                <Box
                  sx={{
                    mt: -4,
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 20,
                  }}
                >
                  <IconButton
                    onClick={() => setShowInProgress(!showInProgress)}
                    aria-label={
                      showInProgress
                        ? "Hide in progress books"
                        : "Show in progress books"
                    }
                    sx={{
                      width: 36,
                      height: 36,
                      boxShadow: 3,
                      backgroundColor: reduxTheme.primary_colors?.[800],
                    }}
                  >
                    {showInProgress ? (
                      <ExpandLess fontSize="large" />
                    ) : (
                      <ExpandMore fontSize="large" />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={showInProgress}>
                  <Box pt={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      In Progress
                    </Typography>

                    <Box position="relative" width="100%" sx={{ px: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          py: 2,
                          overflowX: "auto",
                          scrollSnapType: "x mandatory",
                          scrollbarWidth: "none",
                          "&::-webkit-scrollbar": { display: "none" },
                        }}
                      >
                        {inProgressBooks.map((book) => {
                          const scanned =
                            book.total_comments - book.unfinished_comments;
                          const progress =
                            (scanned / book.total_comments) * 100;

                          return (
                            <Box
                              key={book.id}
                              sx={{
                                minWidth: 244,
                                scrollSnapAlign: "start",
                                backgroundColor:
                                  reduxTheme.primary_colors?.[800],
                                p: 2,
                                borderRadius: 2,
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1}
                              >
                                <Avatar
                                  src={book.thumbnail || `./placeholder.svg`}
                                  sx={{
                                    width: 64,
                                    height: 64,
                                    border: "3px solid rgba(107, 76, 59, 0.15)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  textAlign="center"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {book.title}
                                </Typography>
                              </Box>

                              {/* Comments progress bar */}
                              <Box mt={2}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mb={0.5}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    Progress
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: "#6b4c3b" }}
                                  >
                                    {progress.toFixed(0)}%
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: 8,
                                    backgroundColor: "rgba(107, 76, 59, 0.1)",
                                    borderRadius: 4,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${progress}%`,
                                      height: "90%",
                                      borderRadius: 4,
                                      background:
                                        "linear-gradient(90deg, #6b4c3b, #c29363)",
                                      transition: "width 0.4s ease",
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 0.5,
                                    textAlign: "right",
                                  }}
                                >
                                  {book.total_comments -
                                    book.unfinished_comments}{" "}
                                  / {book.total_comments} done
                                </Typography>
                              </Box>

                              <DueDateBar
                                dueDate={book.dueDate}
                                assignedAt={book.assignedAt}
                              />

                              <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mt: 2 }}
                                onClick={() => handleSelectBook(book)}
                              >
                                Continue
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            )}
          </motion.div>
        )}

        {step === "goal" && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            <IconButton
              onClick={handleClose}
              aria-label="Close"
              sx={{ position: "absolute", top: 16, right: 16 }}
            >
              <Close />
            </IconButton>

            <Typography variant="h4" fontWeight={700} mb={0.5}>
              Daily Goal
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {"Set how many comments you'll scan today"}
            </Typography>

            {selectedBook && (
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  backgroundColor: reduxTheme.primary_colors?.[800],
                }}
              >
                <Avatar
                  src={selectedBook.thumbnail || `./placeholder.svg`}
                  sx={{ width: 48, height: 48 }}
                />

                <Box flex={1}>
                  <Typography fontWeight={600}>{selectedBook.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedBook.total_comments -
                      selectedBook.unfinished_comments}
                    {" / "}
                    {selectedBook.total_comments} scanned
                  </Typography>
                  {/* Due date on goal step */}
                  {selectedBook.dueDate &&
                    (() => {
                      const days = getDaysRemaining(selectedBook.dueDate);
                      const color = getDueDateColor(days);
                      const label = formatDueLabel(days, selectedBook.dueDate);
                      return (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color,
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                      );
                    })()}
                </Box>

                <Box textAlign="right">
                  <Typography variant="caption">Remaining</Typography>
                  <Typography fontWeight={700}>
                    {selectedBook.unfinished_comments}
                  </Typography>
                </Box>
              </Paper>
            )}

            <TextField
              fullWidth
              type="number"
              label="Comments to scan today"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              error={goalError}
              helperText={
                goalError && selectedBook
                  ? `Enter 1–${selectedBook.unfinished_comments}`
                  : " "
              }
              disabled={isSubmittingGoal}
              sx={{
                mb: 4,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {error && (
              <Typography color="error" textAlign="center" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setStep("select");
                  setSelectedBookId(null);
                  setGoalInput("");
                  setError(null);
                }}
                disabled={isSubmittingGoal}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: "12px",
                  textTransform: "none",
                  borderColor: reduxTheme.neutral_colors?.[300] || "#e0e0e0",
                  color: reduxTheme.neutral_colors?.[100] || "#333",
                  "&:hover": {
                    borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                  },
                }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={handleSetGoal}
                disabled={
                  isSubmittingGoal ||
                  !goalInput ||
                  parseInt(goalInput, 10) <= 0 ||
                  parseInt(goalInput, 10) >
                    (selectedBook?.unfinished_comments ?? 0)
                }
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: "12px",
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${
                    reduxTheme.primary_colors?.[500] || "#503629"
                  } 0%, ${reduxTheme.primary_colors?.[400] || "#614438"} 100%)`,
                  "&:hover": {
                    transform: isSubmittingGoal ? "none" : "scale(1.02)",
                  },
                  "&:disabled": {
                    background: `rgba(${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(1, 3),
                            16,
                          )
                        : 97
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(3, 5),
                            16,
                          )
                        : 68
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(5, 7),
                            16,
                          )
                        : 56
                    }, 0.3)`,
                  },
                }}
              >
                {isSubmittingGoal ? "Starting..." : "Start Working"}
              </Button>
            </Box>
          </motion.div>
        )}
      </Box>
    </Dialog>
  );
};

export default MemoryBookLoginModal;
